// ###### IMPORTS
import React, { useState, useCallback, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { TrashIcon, PlusIcon } from '@heroicons/react/24/solid';
import { useAssetsData } from '../../../moduleServices/assets_data_context';
import SpinnerLoad from 'views/finance/moduleComponents/SpinnerLoad';
import { createManyAssetReturns } from '../../viewServices/assets_api';
import { formatApiError } from '../../viewServices/snapshot_form_shared';
import {
  RETURN_PAYMENT_TYPES,
  emptyReturnRow,
  buildReturnsPayloadFromRows,
  formatReturnsCreateManyMessage,
  prefillReturnRowsFromLastMonth,
} from '../../viewServices/return_form_shared';

// ###### COMPONENT — batch returns (Manage / Custom Update accordion §4)
const FormReturnsBatchInline = () => {
  const { assets, returns, refreshFull } = useAssetsData();
  const [rows, setRows] = useState(() => [emptyReturnRow()]);
  const [submitting, setSubmitting] = useState(false);

  const tradeableAssets = useMemo(() => {
    const list = (assets || []).filter((a) => a.asset_type === 'tradeable');
    return [...list].sort((a, b) => {
      const ca = a.asset_code != null ? String(a.asset_code).trim() : '';
      const cb = b.asset_code != null ? String(b.asset_code).trim() : '';
      return ca.localeCompare(cb, undefined, { sensitivity: 'base' });
    });
  }, [assets]);

  const resetForm = useCallback(() => {
    setRows([emptyReturnRow()]);
  }, []);

  const clearRows = useCallback(() => {
    setRows([emptyReturnRow()]);
  }, []);

  const handlePreFillFromLastReturnMonth = useCallback(() => {
    const { rows: nextRows, monthLabel } = prefillReturnRowsFromLastMonth(returns, tradeableAssets);
    if (!monthLabel) {
      toast('No returns with a payment date.');
      return;
    }
    if (nextRows.length === 0) {
      toast(`Nothing to pre-fill for ${monthLabel}.`);
      setRows([emptyReturnRow()]);
      return;
    }
    setRows(nextRows);
    const n = nextRows.length;
    toast.success(
      n === 1
        ? `Pre-filled 1 line · ${monthLabel}`
        : `Pre-filled ${n} lines · ${monthLabel}`
    );
  }, [returns, tradeableAssets]);

  const updateRow = useCallback((key, field, value) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.key !== key) return r;
        const next = { ...r, [field]: value };
        if (field === 'asset' || field === 'payment_type') {
          next.refFromLastReturn = null;
        }
        return next;
      })
    );
  }, []);

  const addRow = useCallback(() => {
    setRows((prev) => [...prev, emptyReturnRow()]);
  }, []);

  const removeRow = useCallback((key) => {
    setRows((prev) => (prev.length <= 1 ? prev : prev.filter((r) => r.key !== key)));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = buildReturnsPayloadFromRows(rows, tradeableAssets);
    if (payload == null) return;

    try {
      setSubmitting(true);
      const res = await createManyAssetReturns(payload);
      const { status, data } = res;
      const created = data?.created_count ?? 0;
      const errs = data?.errors;

      if (status === 207 && errs?.length) {
        toast.error(formatReturnsCreateManyMessage(data));
        if (created > 0) {
          toast.success(
            created === 1 ? '1 return saved.' : `${created} returns saved.`
          );
          resetForm();
          if (refreshFull) await refreshFull();
        }
        return;
      }

      toast.success(
        created === 1 ? '1 return saved.' : `${created} returns saved.`
      );
      resetForm();
      if (refreshFull) await refreshFull();
    } catch (err) {
      toast.error(formatApiError(err.response?.data) || err.message || 'Failed to save returns.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative">
      {submitting && (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-md bg-white/90">
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-lg">
            <SpinnerLoad />
          </div>
        </div>
      )}

      <h3 className="mb-3 text-sm font-semibold text-gray-900">New returns (batch)</h3>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <span className="text-sm font-semibold text-gray-800">
              Items
              <span className="ml-1 text-xs font-normal text-gray-400 tabular-nums">
                ({rows.length})
              </span>
            </span>
            <div className="flex flex-wrap items-center justify-end gap-2">
              <button
                type="button"
                onClick={handlePreFillFromLastReturnMonth}
                disabled={submitting}
                className="inline-flex items-center rounded-lg border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Pre-fill
              </button>
              <button
                type="button"
                onClick={clearRows}
                disabled={submitting}
                className="inline-flex items-center rounded-lg border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Clear
              </button>
              <button
                type="button"
                onClick={addRow}
                disabled={submitting}
                className="inline-flex items-center gap-1 rounded-lg border border-green-200 bg-green-50 px-2 py-1 text-xs font-medium text-green-800 hover:text-green-900"
              >
                <PlusIcon className="h-4 w-4" />
                Row
              </button>
            </div>
          </div>

          <div className="overflow-hidden overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-[700px] w-full text-xs">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wide text-gray-600">
                <tr>
                  <th className="w-8 px-1 py-2 text-left" aria-label="Remove" />
                  <th className="min-w-[7.5rem] max-w-[9.125rem] w-[8rem] px-1 py-2 text-left">Asset</th>
                  <th
                    colSpan={2}
                    className="border-l border-gray-200 bg-gray-50 px-1 py-2 text-left leading-tight"
                    title="Payment date, value and quantity from the latest return in the reference month"
                  >
                    Last month ref.
                  </th>
                  <th className="w-40 px-1 py-2 text-left">Type</th>
                  <th className="w-[6.5rem] min-w-[6.5rem] px-1 py-2 text-left">Date</th>
                  <th className="w-24 px-1 py-2 text-left">R$</th>
                  <th className="w-20 px-1 py-2 text-left">qty</th>
                  <th className="min-w-[5rem] max-w-[7rem] px-1 py-2 text-left">Notes</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.key} className="border-t border-gray-100 align-top">
                    <td className="px-1 py-1.5">
                      <button
                        type="button"
                        onClick={() => removeRow(r.key)}
                        disabled={submitting || rows.length <= 1}
                        className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-30"
                        title="Remove row"
                      >
                        <TrashIcon className="h-3.5 w-3.5" />
                      </button>
                    </td>
                    <td className="w-52 px-1 py-1.5">
                      <select
                        value={r.asset}
                        onChange={(e) => updateRow(r.key, 'asset', e.target.value)}
                        disabled={submitting}
                        className="w-full max-w-full rounded border border-gray-200 bg-white py-0.5 pl-1 pr-0 text-[11px] leading-tight"
                      >
                        <option value="">—</option>
                        {tradeableAssets.map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.asset_code ?? a.id} — {a.asset_name ?? '—'}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="w-[4rem] min-w-[4rem] max-w-[4rem] border-l border-gray-200 bg-gray-50/90 px-1 py-1.5 align-top">
                      {r.refFromLastReturn ? (
                        <span className="block font-bold tabular-nums text-[9px] leading-tight text-gray-900">
                          {r.refFromLastReturn.paymentDate}
                        </span>
                      ) : (
                        <span className="text-[10px] text-gray-300">—</span>
                      )}
                    </td>
                    <td className="w-[4.25rem] min-w-[4.25rem] max-w-[4.25rem] bg-gray-50/90 px-1 py-1.5 align-top">
                      {r.refFromLastReturn ? (
                        <div className="flex flex-col gap-0.5 leading-tight">
                          <span className="block break-all text-[9px] text-gray-700 tabular-nums">
                            R$ {r.refFromLastReturn.valueLabel}
                          </span>
                          <span className="block text-[9px] tabular-nums text-gray-700">
                            <span className="font-medium text-gray-500">qty</span>{' '}
                            {r.refFromLastReturn.qtyLabel}
                          </span>
                        </div>
                      ) : (
                        <span className="text-[10px] text-gray-300">—</span>
                      )}
                    </td>
                    <td className="w-[5.5rem] min-w-[5.5rem] max-w-[5.5rem] px-1 py-1.5">
                      <select
                        value={r.payment_type}
                        onChange={(e) => updateRow(r.key, 'payment_type', e.target.value)}
                        disabled={submitting}
                        className="w-full max-w-full rounded border border-gray-200 bg-white py-0.5 pl-1 pr-0 text-[11px] leading-tight"
                      >
                        {RETURN_PAYMENT_TYPES.map(({ value, label }) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="w-[6.5rem] min-w-[6.5rem] px-1 py-1.5">
                      <input
                        type="date"
                        value={r.payment_date}
                        onChange={(e) => updateRow(r.key, 'payment_date', e.target.value)}
                        disabled={submitting}
                        className="w-full min-w-0 rounded border border-gray-200 px-1 py-0.5 text-[11px] leading-none"
                      />
                    </td>
                    <td className="w-[4.25rem] min-w-[4.25rem] px-1 py-1.5">
                      <input
                        type="text"
                        value={r.payment_value}
                        onChange={(e) => updateRow(r.key, 'payment_value', e.target.value)}
                        disabled={submitting}
                        className="w-full rounded border border-gray-200 px-1 py-0.5 text-[11px] tabular-nums leading-tight"
                        inputMode="decimal"
                        placeholder="0"
                      />
                    </td>
                    <td className="w-[3.25rem] min-w-[3.25rem] px-1 py-1.5">
                      <input
                        type="text"
                        value={r.quantity_at_time}
                        onChange={(e) => updateRow(r.key, 'quantity_at_time', e.target.value)}
                        disabled={submitting}
                        className="w-full rounded border border-gray-200 px-1 py-0.5 text-[11px] tabular-nums leading-tight"
                        inputMode="decimal"
                        placeholder="—"
                      />
                    </td>
                    <td className="min-w-0 max-w-[7rem] px-1 py-1.5">
                      <input
                        type="text"
                        value={r.notes}
                        onChange={(e) => updateRow(r.key, 'notes', e.target.value)}
                        disabled={submitting}
                        className="w-full max-w-full rounded border border-gray-200 px-1 py-0.5 text-[11px] leading-tight"
                        placeholder="…"
                        maxLength={500}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-t border-gray-100 pt-3">
          <p className="min-w-0 flex-1 text-left text-xs text-gray-500">
            Tradeable assets only. Empty rows are ignored. Each filled row needs date and value.
          </p>
          <button
            type="submit"
            disabled={submitting}
            className={`shrink-0 rounded-xl px-5 py-2 text-sm font-medium text-white ${
              submitting ? 'cursor-not-allowed bg-green-400' : 'bg-green-700 hover:bg-green-800'
            }`}
          >
            Save
          </button>
        </div>
      </form>
    </div>
  );
};

export default FormReturnsBatchInline;
